import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, Link2, Loader2, Star, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { mediaUrl } from "@/lib/pricing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PhotoManager = ({ images, onChange }) => {
  const fileRef = useRef(null);
  const [url, setUrl] = useState("");
  const [uploading, setUploading] = useState(0);

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(list.length);
    const added = [];
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await axios.post(`${API}/uploads`, fd);
        added.push(r.data.url);
      } catch (e) {
        toast.error(`${f.name}: ${e.response?.data?.detail || "falha no envio."}`);
      }
      setUploading((n) => n - 1);
    }
    if (added.length) {
      onChange([...images, ...added]);
      toast.success(added.length === 1 ? "1 foto enviada." : `${added.length} fotos enviadas.`);
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const addUrl = () => {
    const u = url.trim();
    if (!/^https?:\/\//.test(u)) return toast.error("Cole um link começando com http.");
    onChange([...images, u]);
    setUrl("");
  };

  const remove = (i) => onChange(images.filter((_, j) => j !== i));
  const makeCover = (i) => onChange([images[i], ...images.filter((_, j) => j !== i)]);

  return (
    <div data-testid="photo-manager">
      <label className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Fotos (a primeira é a capa)</label>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-1">
        {images.map((src, i) => (
          <div key={src + i} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 group" data-testid={`photo-${i}`}>
            <img src={mediaUrl(src)} alt="" className="w-full h-full object-cover" />
            {i === 0 && <span className="absolute bottom-1 left-1 text-[9px] bg-[#0A66C2] text-white px-1.5 rounded font-bold">CAPA</span>}
            <div className="absolute top-1 right-1 flex gap-1">
              {i > 0 && (
                <button type="button" onClick={() => makeCover(i)} title="Usar como capa"
                  className="bg-white/90 shadow p-1 rounded text-amber-500" data-testid={`photo-cover-${i}`}>
                  <Star size={12} />
                </button>
              )}
              <button type="button" onClick={() => remove(i)} title="Remover"
                className="bg-white/90 shadow p-1 rounded text-red-600" data-testid={`photo-remove-${i}`}>
                <X size={12} />
              </button>
            </div>
          </div>
        ))}
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading > 0}
          className="aspect-square rounded-lg border-2 border-dashed border-slate-200 hover:border-[#0A66C2] flex flex-col items-center justify-center text-slate-500 hover:text-[#0A66C2] transition-colors text-[10px] font-bold gap-1"
          data-testid="photo-upload-btn">
          {uploading > 0 ? <Loader2 className="animate-spin" size={20} /> : <ImagePlus size={20} />}
          {uploading > 0 ? `Enviando ${uploading}...` : "Enviar fotos"}
        </button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => upload(e.target.files)} data-testid="photo-file-input" />
      <div className="flex gap-2 mt-2">
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Ou cole o link de uma foto da internet"
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addUrl())}
          className="bg-white border-slate-200 text-xs" data-testid="photo-url-input" />
        <Button type="button" variant="outline" onClick={addUrl} className="border-slate-200 bg-white" data-testid="photo-url-add">
          <Link2 size={14} />
        </Button>
      </div>
    </div>
  );
};
